import { BridgeClient } from 'app/service/bridge/BridgeClient';
import {
  notifyIndexingRequested,
  notifyIndexingRequestFailed,
} from 'features/operation-supervisor/indexingRequestEvents';
import { getLocalizedMessage } from 'util/useLocalizedNotifications';

const normalizeStringExclusions = (terms) => (
  Array.isArray(terms)
    ? terms.map(term => String(term).trim().toLowerCase()).filter(Boolean)
    : null
);

const normalizeIdExclusions = (ids) => (
  Array.isArray(ids) ? ids.map(id => String(id)) : null
);

const sameExclusions = (requested, persisted) => (
  requested.length === persisted.length
  && requested.every((term, index) => term === persisted[index])
);

const assertPersistedExclusions = (settings, persistedSettings) => {
  const fields = [
    ['excluded_term_suggestions', normalizeStringExclusions],
    ['excluded_words', normalizeStringExclusions],
    ['excluded_product_ids', normalizeIdExclusions],
    ['excluded_category_ids', normalizeIdExclusions],
  ];

  fields.forEach(([field, normalize]) => {
    const requested = normalize(settings[field]) || [];
    const persisted = normalize(persistedSettings?.[field]);
    if (
      (requested.length > 0 || persisted !== null)
      && (persisted === null || !sameExclusions(requested, persisted))
    ) {
      const error = new Error(getLocalizedMessage('emptyQuery.settings.saveFailed'));
      error.code = 'EMPTY_QUERY_EXCLUSIONS_NOT_PERSISTED';
      throw error;
    }
  });
};

const assertPersistedAutoFillModes = (settings, persistedSettings) => {
  [
    'auto_fill_products',
    'auto_fill_term_suggestions',
    'auto_fill_categories',
    'auto_fill_words',
  ].forEach((field) => {
    if (
      Object.prototype.hasOwnProperty.call(settings, field)
      && persistedSettings?.[field] !== settings[field]
    ) {
      const error = new Error(getLocalizedMessage('emptyQuery.settings.saveFailed'));
      error.code = 'EMPTY_QUERY_AUTOFILL_NOT_PERSISTED';
      throw error;
    }
  });
};

/**
 * Creates an empty-query API backed by the bridge transport.
 *
 * Bridge actions used:
 *   empty_query.settings.get          → BridgeUseCase → EmptyQueryStorage (load settings)
 *   empty_query.settings.save         → BridgeUseCase → EmptyQueryStorage (persist settings)
 *   empty_query.artifact.get          → BridgeUseCase → EmptyQueryStorage (load artifact summary)
 *   empty_query.categories.search     → BridgeUseCase → CategoryLookupProvider (search by name/ID)
 *   empty_query.categories.resolve    → BridgeUseCase → CategoryLookupProvider (resolve IDs to objects)
 */
export function createBridgeEmptyQueryApi() {
  const client = new BridgeClient();

  return {
    async getSettings() {
      const response = await client.sendMessageWithResponse('empty_query.settings.get', {});
      return response.settings;
    },

    async saveSettings(settings) {
      const response = await client.sendMessageWithResponse('empty_query.settings.save', settings);
      assertPersistedExclusions(settings, response?.settings);
      assertPersistedAutoFillModes(settings, response?.settings);
      return response.settings;
    },

    async getArtifact() {
      const response = await client.sendMessageWithResponse('empty_query.artifact.get', {});
      const artifact = response.artifact || {};
      return {
        ...artifact,
        product_preview: artifact.product_preview || [],
      };
    },

    async startReindex() {
      notifyIndexingRequested({ source: 'bridge-empty-query-api' });
      let response;
      try {
        response = await client.sendMessageWithResponse('index.startFull', {});
      } catch (err) {
        notifyIndexingRequestFailed({
          source: 'bridge-empty-query-api',
          error: err?.message || getLocalizedMessage('catalog.update.startIndexingFailed'),
        });
        throw err;
      }
      if (response?.result === 'error') {
        const message = response?.error || getLocalizedMessage('catalog.update.startIndexingFailed');
        notifyIndexingRequestFailed({
          source: 'bridge-empty-query-api',
          error: message,
        });
      }
      return response;
    },

    async searchCategories(query, limit = 8) {
      const response = await client.sendMessageWithResponse('empty_query.categories.search', { query, limit });
      return response.items || [];
    },

    async resolveCategories(ids) {
      const response = await client.sendMessageWithResponse('empty_query.categories.resolve', { ids });
      return response.items || [];
    },
  };
}
